import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";
import {
  BarChart3, Award, Smile, Meh, Frown, AlertTriangle, Ban, X, Home, Store,
  Stethoscope, User, HelpCircle, Play, ArrowRight, Zap,
} from "lucide-react";
import {
  loadSpeak,
  aggregateSpeak,
  cueDistribution,
  speakTimeline,
  recordSpeakSession,
  recordSpeakFeedback,
} from "@/lib/neuroSpeakStore";
import VoiceRecorder from "@/components/neurospeak/VoiceRecorder";

// -----------------------------------------------------------------------
// NeuroSpeak — Progress dashboard, Task Assistance profile, Functional
// scenarios and Session Feedback modal.
// -----------------------------------------------------------------------

const KIND_LABELS = {
  retrieval: "Word retrieval",
  recognition: "Word recognition",
  sentence: "Sentence",
  functional: "Functional",
  "picture-word-sentence": "Picture → Sentence",
};

// =======================================================================
// COMMUNICATION PROFILE
// =======================================================================

export function CommunicationProfile({ state }) {
  const s = state || loadSpeak();
  const agg = useMemo(() => aggregateSpeak(s), [s]);
  const timeline = useMemo(() => speakTimeline(s), [s]);
  const kindStats = useMemo(() => {
    const buckets = {};
    (s.sessions || []).forEach((x) => {
      const k = x.kind || "retrieval";
      if (!buckets[k]) buckets[k] = { correct: 0, total: 0 };
      buckets[k].total += 1;
      if (x.correct) buckets[k].correct += 1;
    });
    return Object.entries(buckets).map(([k, v]) => ({
      key: k,
      label: KIND_LABELS[k] || k,
      pct: v.total ? Math.round((v.correct / v.total) * 100) : 0,
      correct: v.correct,
      total: v.total,
    }));
  }, [s]);

  return (
    <div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
          My communication profile · task-specific
        </div>
        <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
          Where you&apos;re growing.
        </h3>
        <p className="mt-2 text-xs text-zinc-500 max-w-2xl">
          Task-performance only. Not a clinical language assessment. A
          speech-language pathologist can help you interpret patterns.
        </p>

        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="Sessions" val={agg.total_sessions} color="#22d3ee" />
          <Stat label="Naming acc." val={`${agg.naming_accuracy}%`} color="#34d399" />
          <Stat label="Sentence acc." val={`${agg.sentence_accuracy}%`} color="#a78bfa" />
          <Stat label="Cue dep." val={`${agg.cue_dependence}%`} color="#fbbf24" />
        </div>

        <div className="mt-6 h-64 rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="speak-timeline">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeline} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="day" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
              <Tooltip
                contentStyle={{ background: "#0A0A14", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12, color: "#fff" }}
              />
              <Line type="monotone" dataKey="naming" stroke="#34d399" strokeWidth={2} dot={{ r: 2 }} name="Naming %" />
              <Line type="monotone" dataKey="sentence" stroke="#a78bfa" strokeWidth={2} dot={{ r: 2 }} name="Sentence %" />
              <Line type="monotone" dataKey="cue" stroke="#fbbf24" strokeWidth={2} dot={{ r: 2 }} name="Cue dep. %" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-zinc-400">
          <Legend color="#34d399" label="Naming accuracy" />
          <Legend color="#a78bfa" label="Sentence accuracy" />
          <Legend color="#fbbf24" label="Cue dependence" />
        </div>
      </div>

      {/* By-task breakdown */}
      <div className="mt-4 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Task-specific performance
        </div>
        {kindStats.length === 0 ? (
          <p className="mt-3 text-sm text-zinc-500">
            No sessions yet — start with Baseline or the 2-min demo.
          </p>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {kindStats.map((k) => (
              <div
                key={k.key}
                data-testid={`speak-kind-${k.key}`}
                className="rounded-2xl border border-white/10 bg-black/30 p-4"
              >
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  {k.label}
                </div>
                <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-white">
                  {k.correct}/{k.total}
                </div>
                <div className="text-xs text-zinc-500">{k.pct}% correct</div>
                <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-400"
                    style={{ width: `${k.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =======================================================================
// TASK ASSISTANCE PROFILE — cue distribution chart
// =======================================================================

export function TaskAssistance({ state }) {
  const s = state || loadSpeak();
  const dist = useMemo(() => cueDistribution(s, 20), [s]);
  const dominant = [...dist].sort((a, b) => b.value - a.value)[0];
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
        Task Assistance Profile
      </div>
      <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
        How much help did you need?
      </h3>
      <p className="mt-2 text-xs text-zinc-500 max-w-2xl">
        Distribution of assistance levels across your last 20 word-retrieval
        attempts. Lower cue levels indicate more independent responses. Not a
        clinical recovery score.
      </p>

      <div className="mt-6 h-52 rounded-2xl border border-white/10 bg-black/30 p-3" data-testid="task-assistance-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dist} margin={{ top: 6, right: 6, left: -14, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
            <Tooltip contentStyle={{ background: "#0A0A14", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12, color: "#fff" }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {dist.map((d, i) => (
                <Cell key={i} fill={d.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 text-xs text-zinc-400">
        Dominant assistance level:{" "}
        <span className="text-white font-semibold">{dominant?.label || "—"}</span>{" "}
        ({dominant?.value || 0}%)
      </div>
    </div>
  );
}

// =======================================================================
// FUNCTIONAL SCENARIOS — everyday communication practice
// =======================================================================

const SCENARIOS = [
  {
    key: "home",
    Icon: Home,
    color: "#22d3ee",
    setting: "At home",
    prompt: "You are at home and you feel thirsty. What would you say?",
    accept: ["i want water", "water please", "give me water", "i need water"],
    exemplar: "I want water.",
  },
  {
    key: "shop",
    Icon: Store,
    color: "#a78bfa",
    setting: "At the shop",
    prompt: "You are at a shop and want to buy bread. What would you say?",
    accept: ["i want bread", "bread please", "one bread", "can i have bread"],
    exemplar: "I want bread.",
  },
  {
    key: "hospital",
    Icon: Stethoscope,
    color: "#f87171",
    setting: "At the hospital",
    prompt: "You feel unwell at the hospital. How would you tell someone?",
    accept: ["i am feeling uncomfortable", "i feel unwell", "i am not okay", "i am unwell", "not well"],
    exemplar: "I am feeling uncomfortable.",
  },
  {
    key: "family",
    Icon: User,
    color: "#34d399",
    setting: "With family",
    prompt: "You want to talk to your daughter. What would you say?",
    accept: ["i want to talk to my daughter", "call my daughter", "let me talk to my daughter", "talk to daughter"],
    exemplar: "I want to talk to my daughter.",
  },
  {
    key: "help",
    Icon: HelpCircle,
    color: "#fbbf24",
    setting: "Asking for help",
    prompt: "You need help urgently. What would you say?",
    accept: ["please help me", "help me", "i need help", "i need assistance"],
    exemplar: "Please help me.",
  },
];

export function FunctionalScenarios({ onFeedback }) {
  const [idx, setIdx] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [phase, setPhase] = useState("play"); // play -> reveal
  const [correct, setCorrect] = useState(0);
  const [done, setDone] = useState(false);
  const [lang] = useState(() => loadSpeak().profile?.language || "en");
  const sc = SCENARIOS[idx];

  const handleVoice = (data) => {
    const heard = (data.transcript || "").trim();
    setTranscript(heard);
    const norm = heard.toLowerCase();
    const ok = sc.accept.some((a) => norm.includes(a));
    if (ok) setCorrect((c) => c + 1);
    recordSpeakSession({
      kind: "functional",
      word: sc.exemplar,
      response: heard,
      cue_level: 0,
      correct: ok,
    });
    setPhase("reveal");
  };

  const next = () => {
    if (idx + 1 >= SCENARIOS.length) {
      setDone(true);
      onFeedback?.();
    } else {
      setIdx(idx + 1);
      setTranscript("");
      setPhase("play");
    }
  };

  if (done) {
    return (
      <div className="rounded-3xl border border-emerald-400/30 bg-emerald-400/5 p-6 sm:p-8">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Functional scenarios complete
        </div>
        <div className="mt-1 font-display text-2xl font-semibold">
          {correct}/{SCENARIOS.length} appropriate responses
        </div>
        <div className="text-xs text-zinc-400 mt-1">
          Task-performance only. Real-world communication depends on many factors.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold inline-flex items-center gap-1.5">
        <sc.Icon className="w-3.5 h-3.5" style={{ color: sc.color }} />
        Scenario {idx + 1} / {SCENARIOS.length} · {sc.setting}
      </div>
      <p className="mt-3 text-white text-lg">{sc.prompt}</p>

      {phase === "play" && (
        <div className="mt-4">
          <VoiceRecorder
            key={idx}
            target=""
            language={lang}
            onResult={handleVoice}
            testId="scenario-voice"
            ctaText="Tap to Speak your reply"
            maxMs={10000}
          />
        </div>
      )}

      {phase === "reveal" && (
        <>
          {transcript && (
            <div className="mt-4 rounded-2xl border border-white/10 bg-black/40 p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                You said
              </div>
              <div className="mt-1 text-lg text-white">{transcript}</div>
            </div>
          )}
          <div className="mt-4 rounded-2xl border border-cyan-400/25 bg-cyan-400/5 p-4">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
              Model answer
            </div>
            <div className="mt-1 font-display text-xl text-white">{sc.exemplar}</div>
          </div>
          <button
            onClick={next}
            data-testid="scenario-next"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-cyan-500 text-black font-medium px-5 py-2 text-sm hover:bg-cyan-400"
          >
            Next scenario <ArrowRight className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
}

// =======================================================================
// SESSION FEEDBACK MODAL — post-activity self-report
// =======================================================================

const EASE_OPTIONS = [
  { key: "easy", label: "Easy", Icon: Smile, color: "#34d399" },
  { key: "comfortable", label: "Comfortable", Icon: Smile, color: "#67e8f9" },
  { key: "challenging", label: "Challenging", Icon: Meh, color: "#fbbf24" },
  { key: "hard", label: "Very difficult", Icon: Frown, color: "#f97316" },
  { key: "unable", label: "Unable to complete", Icon: Ban, color: "#f87171" },
];

export function SpeakFeedbackModal({ open, onClose }) {
  const [ease, setEase] = useState(null);
  const [showReport, setShowReport] = useState(false);

  const submit = () => {
    if (!ease) return;
    recordSpeakFeedback(ease, "");
    setEase(null);
    setShowReport(false);
    onClose?.();
  };

  const close = () => {
    setEase(null);
    setShowReport(false);
    onClose?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={close}
          data-testid="speak-feedback-modal"
        >
          <motion.div
            initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A14] p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
                  Quick check-in
                </div>
                <h3 className="mt-1 font-display text-xl font-semibold">
                  How did this activity feel?
                </h3>
              </div>
              <button
                data-testid="speak-feedback-close"
                onClick={close}
                className="w-8 h-8 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-4 space-y-1.5">
              {EASE_OPTIONS.map((o) => {
                const on = ease === o.key;
                return (
                  <button
                    key={o.key}
                    data-testid={`speak-feedback-${o.key}`}
                    onClick={() => setEase(o.key)}
                    className={`w-full text-left rounded-xl border px-3 py-2.5 flex items-center gap-2 ${
                      on
                        ? "border-cyan-400 bg-cyan-400/15 text-white"
                        : "border-white/10 bg-white/5 hover:bg-white/10 text-zinc-200"
                    }`}
                  >
                    <o.Icon className="w-4 h-4" style={{ color: o.color }} />
                    <span className="text-sm">{o.label}</span>
                  </button>
                );
              })}
            </div>

            <button
              data-testid="speak-feedback-report-toggle"
              onClick={() => setShowReport(!showReport)}
              className="mt-3 text-xs text-amber-300 hover:text-amber-200 inline-flex items-center gap-1"
            >
              <AlertTriangle className="w-3 h-3" /> Report difficulty or discomfort
            </button>
            {showReport && (
              <div
                data-testid="speak-feedback-note"
                className="mt-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-100"
              >
                Your caregiver or clinician can see that you flagged this
                session as difficult. If you would like to record a voice note
                instead, use the microphone on the main NeuroSpeak page — the
                session log is already captured.
              </div>
            )}

            {ease === "unable" || ease === "hard" ? (
              <div className="mt-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100">
                If this activity felt very difficult or you experienced new or
                worsening symptoms, please stop and contact your healthcare
                professional. Emergency 108 is always one tap away.
              </div>
            ) : null}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                data-testid="speak-feedback-skip"
                onClick={close}
                className="text-xs text-zinc-400 hover:text-white px-3 py-2"
              >
                Skip
              </button>
              <button
                data-testid="speak-feedback-submit"
                onClick={submit}
                disabled={!ease}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400 text-black font-medium px-5 py-2 text-sm hover:bg-emerald-300 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// -----------------------------------------------------------------------
// Local helpers
// -----------------------------------------------------------------------
const Stat = ({ label, val, color }) => (
  <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}
    </div>
    <div className="mt-1 font-display text-2xl font-semibold tabular-nums" style={{ color }}>
      {val}
    </div>
  </div>
);
const Legend = ({ color, label }) => (
  <div className="inline-flex items-center gap-2">
    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
    {label}
  </div>
);

// -----------------------------------------------------------------------
// Unified Recovery Journey ribbon (Chunk E kickoff)
// -----------------------------------------------------------------------

export function RecoveryJourneyRibbon() {
  const steps = [
    { label: "Prevention", color: "#22d3ee" },
    { label: "Assessment", color: "#67e8f9" },
    { label: "Cognitive Training", color: "#a78bfa" },
    { label: "Communication Training", color: "#34d399" },
    { label: "Functional Tasks", color: "#fbbf24" },
    { label: "Progress", color: "#f472b6" },
    { label: "Caregiver Support", color: "#60a5fa" },
    { label: "Professional Review", color: "#f87171" },
  ];
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
        My recovery journey
      </div>
      <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
        Prevent → Assess → Rehabilitate → Communicate → Measure → Support.
      </h3>
      <div className="mt-5 flex flex-wrap items-center gap-2">
        {steps.map((s, i) => (
          <div key={s.label} className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest"
              style={{ borderColor: `${s.color}55`, background: `${s.color}15`, color: s.color }}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
              {s.label}
            </span>
            {i < steps.length - 1 && <span className="text-zinc-600">·</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
